import { useIsCollectionsEnabled } from '@modules/common/hooks';
import {
  FeedbackCardMetadataExpandedView,
  FeedbackCardMetadataPreview,
} from '@modules/feedback/components/FeedbackCard/FeedbackCardMetadata';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { trackFeedbackMetadataEvent } from '@modules/feedback/utils';
import { useIsSnippetOrFeedbackSaved } from '@modules/savedFeedbackList/hooks';
import { ExpandMoreRounded } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';

interface FeedbackCardMetadataProps {
  feedback: Feedback;
  sentimentLabel: FeedbackLabel | undefined;
  feedbackListViewType: FeedbackListViewType;
}

export const FeedbackCardMetadata = ({
  feedback,
  sentimentLabel,
  feedbackListViewType,
}: FeedbackCardMetadataProps) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const isCollectionsEnabled = useIsCollectionsEnabled();

  const isSavedFeedback = useIsSnippetOrFeedbackSaved({
    feedbackID: feedback.id,
  });

  const shouldShowSavedFeedbackColor =
    isSavedFeedback &&
    feedbackListViewType === FeedbackListViewType.DEFAULT &&
    !isCollectionsEnabled;

  const handleAccordionChange = (
    _: React.SyntheticEvent<Element, Event>,
    isExpanded: boolean,
  ) => {
    setIsAccordionExpanded(isExpanded);
    if (isExpanded) {
      trackFeedbackMetadataEvent({
        eventName: 'Feedback Metadata - Feedback List Metadata Row Expanded',
        feedbackID: feedback.id,
      });
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      TransitionProps={{
        unmountOnExit: true,
      }}
      expanded={isAccordionExpanded}
      onChange={handleAccordionChange}
      sx={{
        '& .MuiAccordionSummary-content': {
          overflow: 'hidden',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded />}
        sx={{
          padding: 0,
          backgroundColor: shouldShowSavedFeedbackColor
            ? 'feedback.saved'
            : undefined,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          width="100%"
        >
          <Box width="75%" overflow="hidden">
            <FeedbackCardMetadataPreview
              feedback={feedback}
              sentimentLabel={sentimentLabel}
            />
          </Box>
          <Typography variant="body2" noWrap>
            {isAccordionExpanded ? 'Hide metadata' : 'Show metadata'}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: 0,
          backgroundColor: shouldShowSavedFeedbackColor
            ? 'feedback.saved'
            : undefined,
        }}
      >
        <FeedbackCardMetadataExpandedView feedback={feedback} />
      </AccordionDetails>
    </Accordion>
  );
};
